/**
 * A section of a settings window: a heading that folds its rows away and
 * remembers whether it is open (localStorage, by the group's id), so a long
 * window — the Photoreal window's libraries and pools, the view windows'
 * many groups — shows what the user is working on and no more.
 */

import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const OPEN_KEY = 'designer.render.groups.v1';

function readOpen(): Record<string, boolean> {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(OPEN_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch { return {}; }
}

function writeOpen(id: string, open: boolean): void {
  try {
    window.localStorage.setItem(OPEN_KEY, JSON.stringify({ ...readOpen(), [id]: open }));
  } catch { /* not kept — fine */ }
}

export interface GroupProps {
  /** Stable, unique across the windows: what the open state is remembered by. */
  id: string;
  title: string;
  note?: string;
  /** Open the first time (default true); libraries and pools start folded. */
  defaultOpen?: boolean;
  /** Drawn dimmed (a group whose switch is off). */
  off?: boolean;
  /** After the title: a switch, say. Clicks there do not fold the group. */
  headEnd?: React.ReactNode;
  children: React.ReactNode;
}

export const Group: React.FC<GroupProps> = ({ id, title, note, defaultOpen = true, off = false, headEnd, children }) => {
  const [open, setOpen] = useState<boolean>(() => readOpen()[id] ?? defaultOpen);
  const toggle = () => setOpen((o) => { writeOpen(id, !o); return !o; });
  return (
    <section className={`rc-group${off ? ' is-off' : ''}${open ? ' is-open' : ''}`}>
      <div className="rc-group-head">
        <button type="button" className="rc-group-toggle" aria-expanded={open} onClick={toggle}>
          <ChevronDown size={13} className="rc-group-chevron" aria-hidden="true" />
          <span className="rc-group-title">{title}</span>
        </button>
        {headEnd}
      </div>
      {open && (
        <div className="rc-group-body">
          {note && <p className="rc-note" style={{ marginTop: 0 }}>{note}</p>}
          {children}
        </div>
      )}
    </section>
  );
};
